import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const FREE_LIMIT               = 10;  // max saved prompts (entries) for free users
export const FREE_SKILL_CREATE_LIMIT  = 5;   // max skills a free user can CREATE (not install)
export const FREE_PLAYBOOK_CREATE_LIMIT = 2; // max playbooks a free user can CREATE (not install)
export const FREE_FOLDER_LIMIT        = 2;   // max folders for free users
export const FREE_SKILL_INSTALL       = 3;   // max skill INSTALLS from marketplace (free)
export const FREE_PLAYBOOK_INSTALL    = 1;   // max playbook INSTALLS from marketplace (free)
// Aliases used by MarketplacePage (keep in sync)
export const FREE_SKILL_LIMIT         = FREE_SKILL_INSTALL;
export const FREE_PLAYBOOK_LIMIT      = FREE_PLAYBOOK_INSTALL;

export function AuthProvider({ children }) {
  const [session, setSession]   = useState(undefined);
  const [profile, setProfile]   = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session) loadProfile(data.session.user.id);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) loadProfile(session.user.id);
      else setProfile(null);
    });

    // Re-fetch profile when window regains focus so plan changes by admin
    // are reflected without requiring a full page reload
    const handleFocus = () => {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session?.user?.id) loadProfile(data.session.user.id);
      });
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      listener.subscription.unsubscribe();
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data);
  };

  const refreshProfile = async () => {
    if (session?.user?.id) await loadProfile(session.user.id);
  };

  const signUp = async (email, password, username) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;

    // If email confirmation is OFF: data.session is set, user is live immediately
    // If email confirmation is ON:  data.session is null, user must confirm email first
    // Either way we return data — the UI handles both cases
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithGitHub = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updateEmail = async (newEmail) => {
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const updateProfile = async (fields) => {
    const { error } = await supabase
      .from('profiles')
      .update(fields)
      .eq('id', session.user.id);
    if (error) throw error;
    await refreshProfile();
  };

  // isPro: plan='pro' OR active trial (trial_expires_at in the future)
  // trial_expires_at is a new column — see migration below
  const trialActive = profile?.trial_expires_at
    ? new Date(profile.trial_expires_at) > new Date()
    : false;
  const isPro = profile?.plan === 'pro' || trialActive;
  const isTrial = trialActive && profile?.plan !== 'pro'; // on trial but not paid
  const isAdmin            = profile?.role === 'admin';
  const hasSeenOnboarding  = profile?.has_seen_onboarding;

  // Teams subscription — lives on profile, not on individual teams
  const hasTeamsPlan       = isAdmin || (!!(profile?.teams_plan) && profile?.teams_billing_status === 'active');
  const teamsPlan          = profile?.teams_plan ?? null;          // 'starter'|'growth'|'agency'|null
  const teamsSeatLimit     = profile?.teams_seat_limit ?? 0;
  const teamsBillingStatus = profile?.teams_billing_status ?? null;

  // True when profile username looks like an auto-generated email prefix
  // Used to redirect Google OAuth users to username setup
  const needsUsernameSetup = (() => {
    if (!profile || !session?.user) return false;
    const emailPrefix = session.user.email?.split('@')[0]?.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const username = profile.username?.toLowerCase();
    return !username || username === emailPrefix || /^\w+\d+$/.test(username);
  })();
  const isLoading = session === undefined;
  const entryCount = profile?.entry_count ?? 0;
  const canAddEntry    = isPro || isAdmin || entryCount < FREE_LIMIT;
  const remainingFree  = Math.max(FREE_LIMIT - entryCount, 0);

  // Skill creation: free users can create up to FREE_SKILL_CREATE_LIMIT skills
  // skillCount comes from profile or we rely on the page to check separately
  const canCreateSkill   = isPro || isAdmin;  // unlimited for Pro; free checked in page
  const canPublishToMarket = isPro || isAdmin; // publish to marketplace = Pro only
  const canMakePrivate   = isPro || isAdmin;   // private skills = Pro only

  return (
    <AuthContext.Provider value={{
      session, profile, isAdmin, isPro, isTrial, isLoading, needsUsernameSetup, hasSeenOnboarding,
      canCreateSkill, canPublishToMarket, canMakePrivate,
      hasTeamsPlan, teamsPlan, teamsSeatLimit, teamsBillingStatus,
      entryCount, canAddEntry, remainingFree,
      signUp, signIn, signOut, resetPassword, signInWithGoogle, signInWithGitHub,
      updateEmail, updatePassword, updateProfile, refreshProfile,
      user: session?.user ?? null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
